// Property class cache utility for storing and retrieving property class labels
// This provides a local cache system for property class labels to improve performance

export interface PropertyClassLabel {
  id: string;
  label: string;
  apiValue: string; // API value for homeType parameter
  dbLabel: string; // Database label (e.g., "SingleFamily", "VacantLand")
  category: 'rent' | 'buy' | 'sold';
  isActive: boolean;
  order: number;
}

// Default property class labels based on the existing constants
// Now includes both UI labels and database labels for comprehensive matching
const DEFAULT_RENT_PROPERTY_CLASSES: PropertyClassLabel[] = [
  { id: 'apartment', label: 'Apartments/Condos', apiValue: 'apartment', dbLabel: 'Apartment', category: 'rent', isActive: true, order: 1 },
  { id: 'single-family', label: 'Single family home', apiValue: 'singleFamily', dbLabel: 'SingleFamily', category: 'rent', isActive: true, order: 2 },
  { id: 'condo', label: 'Condo', apiValue: 'condo', dbLabel: 'Condo', category: 'rent', isActive: false, order: 3 },
  { id: 'townhome', label: 'Townhome', apiValue: 'townhome', dbLabel: 'Townhouse', category: 'rent', isActive: true, order: 4 },
  { id: 'multi-family', label: 'Multi family home', apiValue: 'multiFamily', dbLabel: 'MultiFamily', category: 'rent', isActive: false, order: 5 },
];

const DEFAULT_BUY_PROPERTY_CLASSES: PropertyClassLabel[] = [
  { id: 'single-family', label: 'Single family home', apiValue: 'singleFamily', dbLabel: 'SingleFamily', category: 'buy', isActive: true, order: 1 },
  { id: 'condo', label: 'Condo', apiValue: 'condo', dbLabel: 'Condo', category: 'buy', isActive: true, order: 2 },
  { id: 'townhome', label: 'Townhome', apiValue: 'townhome', dbLabel: 'Townhouse', category: 'buy', isActive: true, order: 3 },
  { id: 'multi-family', label: 'Multi family home', apiValue: 'multiFamily', dbLabel: 'MultiFamily', category: 'buy', isActive: true, order: 4 },
  { id: 'land', label: 'Land', apiValue: 'land', dbLabel: 'VacantLand', category: 'buy', isActive: true, order: 5 },
];

const DEFAULT_SOLD_PROPERTY_CLASSES: PropertyClassLabel[] = [
  { id: 'single-family', label: 'Single family home', apiValue: 'singleFamily', dbLabel: 'SingleFamily', category: 'sold', isActive: true, order: 1 },
  { id: 'condo', label: 'Condo', apiValue: 'condo', dbLabel: 'Condo', category: 'sold', isActive: true, order: 2 },
  { id: 'townhome', label: 'Townhome', apiValue: 'townhome', dbLabel: 'Townhouse', category: 'sold', isActive: true, order: 3 },
  { id: 'multi-family', label: 'Multi family home', apiValue: 'multiFamily', dbLabel: 'MultiFamily', category: 'sold', isActive: true, order: 4 },
  { id: 'land', label: 'Land', apiValue: 'land', dbLabel: 'VacantLand', category: 'sold', isActive: true, order: 5 },
];

// Cache key for localStorage
const CACHE_KEY = 'propertyClassLabels';

// Cache duration in milliseconds (24 hours)
const CACHE_DURATION = 24 * 60 * 60 * 1000;

export interface PropertyClassCache {
  data: PropertyClassLabel[];
  timestamp: number;
  version: string;
}

// Get cached property class labels
export function getCachedPropertyClassLabels(): PropertyClassLabel[] {
  if (typeof window === 'undefined') {
    return [...DEFAULT_RENT_PROPERTY_CLASSES, ...DEFAULT_BUY_PROPERTY_CLASSES, ...DEFAULT_SOLD_PROPERTY_CLASSES];
  }

  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const cache: PropertyClassCache = JSON.parse(cached);
      const now = Date.now();
      
      // Check if cache is still valid
      if (now - cache.timestamp < CACHE_DURATION) {
        return cache.data;
      }
    }
  } catch (error) {
    // Cache load failed, will use defaults
  }

  // Return default labels if cache is invalid or doesn't exist
  return [...DEFAULT_RENT_PROPERTY_CLASSES, ...DEFAULT_BUY_PROPERTY_CLASSES, ...DEFAULT_SOLD_PROPERTY_CLASSES];
}

// Save property class labels to cache
export function savePropertyClassLabelsToCache(labels: PropertyClassLabel[]): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const cache: PropertyClassCache = {
      data: labels,
      timestamp: Date.now(),
      version: '1.0.0'
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    // Cache save failed, continue without caching
  }
}

// Get property class labels for a specific category
export function getPropertyClassLabelsForCategory(category: 'rent' | 'buy' | 'sold'): PropertyClassLabel[] {
  const allLabels = getCachedPropertyClassLabels();
  return allLabels
    .filter(label => label.category === category && label.isActive)
    .sort((a, b) => a.order - b.order);
}

// Get property class labels as simple string arrays (for backward compatibility)
export function getPropertyClassLabelsAsStrings(category: 'rent' | 'buy' | 'sold'): string[] {
  return getPropertyClassLabelsForCategory(category).map(label => label.label);
}

// Get property class API values as simple string arrays
export function getPropertyClassApiValuesAsStrings(category: 'rent' | 'buy' | 'sold'): string[] {
  return getPropertyClassLabelsForCategory(category).map(label => label.apiValue);
}

// Convert selected UI labels to API values for homeType parameter
export function convertSelectedPropertyTypesToApiString(selectedLabels: string[], category: 'rent' | 'buy' | 'sold'): string {
  const allLabels = getPropertyClassLabelsForCategory(category);
  
  
  const apiValues = selectedLabels
    .map(label => {
      const propertyClass = allLabels.find(pc => pc.label === label);
      const apiValue = propertyClass?.apiValue;
      
      
      return apiValue;
    })
    .filter(Boolean) as string[];
  
  const result = apiValues.join(',');
  
  
  return result;
}

// Update property class label
export function updatePropertyClassLabel(id: string, updates: Partial<PropertyClassLabel>): void {
  const allLabels = getCachedPropertyClassLabels();
  const updatedLabels = allLabels.map(label => 
    label.id === id ? { ...label, ...updates } : label
  );
  savePropertyClassLabelsToCache(updatedLabels);
}

// Add new property class label
export function addPropertyClassLabel(label: Omit<PropertyClassLabel, 'id'>): void {
  const allLabels = getCachedPropertyClassLabels();
  const newLabel: PropertyClassLabel = {
    ...label,
    id: `${label.category}-${label.label.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}`
  };
  allLabels.push(newLabel);
  savePropertyClassLabelsToCache(allLabels);
}

// Remove property class label (soft delete by setting isActive to false)
export function removePropertyClassLabel(id: string): void {
  updatePropertyClassLabel(id, { isActive: false });
}

// Clear cache and reset to defaults
export function clearPropertyClassCache(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.removeItem(CACHE_KEY);
  } catch (error) {
    // Cache clear failed, continue
  }
}

// Initialize cache with default values if it doesn't exist
export function initializePropertyClassCache(): void {
  const cached = getCachedPropertyClassLabels();
  if (cached.length === 0) {
    const defaultLabels = [...DEFAULT_RENT_PROPERTY_CLASSES, ...DEFAULT_BUY_PROPERTY_CLASSES, ...DEFAULT_SOLD_PROPERTY_CLASSES];
    savePropertyClassLabelsToCache(defaultLabels);
  }
}

/**
 * Check if a property type matches any of the selected property types
 * This function handles both UI labels and database labels for comprehensive matching
 * @param propertyType - The property type to check (could be UI label or DB label)
 * @param selectedPropertyTypes - Array of selected UI labels
 * @param category - The category to check against
 * @returns boolean indicating if the property type matches
 */
export function isPropertyTypeMatch(propertyType: string, selectedPropertyTypes: string[], category: 'rent' | 'buy' | 'sold'): boolean {
  if (!propertyType || selectedPropertyTypes.length === 0) {
    return true; // No filter applied
  }

  const allLabels = getPropertyClassLabelsForCategory(category);
  
  // Check if the property type matches any of the selected types
  // This handles both UI labels and database labels
  return selectedPropertyTypes.some(selectedType => {
    const propertyClass = allLabels.find(pc => pc.label === selectedType);
    if (!propertyClass) return false;
    
    // Check if property type matches UI label, API value, or database label
    return propertyType === propertyClass.label || 
           propertyType === propertyClass.apiValue || 
           propertyType === propertyClass.dbLabel;
  });
}

/**
 * Get all possible property type variations for a given UI label
 * @param uiLabel - The UI label to get variations for
 * @param category - The category to check
 * @returns Array of all possible property type strings that match this UI label
 */
export function getPropertyTypeVariations(uiLabel: string, category: 'rent' | 'buy' | 'sold'): string[] {
  const allLabels = getPropertyClassLabelsForCategory(category);
  const propertyClass = allLabels.find(pc => pc.label === uiLabel);
  
  if (!propertyClass) return [uiLabel]; // Fallback to original label
  
  return [
    propertyClass.label,    // UI label: "Single family home"
    propertyClass.apiValue, // API value: "singleFamily"
    propertyClass.dbLabel   // DB label: "SingleFamily"
  ];
}

/**
 * Normalize a property type to its UI label
 * @param propertyType - The property type to normalize (could be any format)
 * @param category - The category to check against
 * @returns The normalized UI label, or the original if no match found
 */
export function normalizePropertyTypeToUI(propertyType: string, category: 'rent' | 'buy' | 'sold'): string {
  const allLabels = getPropertyClassLabelsForCategory(category);
  
  // Find the property class that matches this property type
  const propertyClass = allLabels.find(pc => 
    pc.label === propertyType || 
    pc.apiValue === propertyType || 
    pc.dbLabel === propertyType
  );
  
  return propertyClass?.label || propertyType; // Return UI label or original if no match
}
